import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, UncontrolledTooltip } from 'reactstrap';
import withRouter from '../.././components/withRouter';
import TableContainer from '../.././components/TableContainerReactTable';
import { useProfile } from '../../hooks/UserHooks';
import { deleteCreative, getCreatives } from '../../api/creativeApi';
import { Creative, CreativeType } from '../../types/creative';
import AddEditCreativeModal from './AddEditCreativeModal';
import PreviewCreativeModal from './PreviewCreativeModal';
import { toast } from 'react-toastify';

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Creative[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Creative|null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);
    const [isPreviewCreativeModalOpen, setIsPreviewCreativeModalOpen] = useState<boolean>(false);

    useEffect(() => {
        getCreatives().then(setData).finally(() => setLoading(false));
    }, []);

    const handleAddUpdateData = (newData: Creative) => {
        setLoading(true);
        getCreatives().then(setData).finally(() => setLoading(false));
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const togglePreviewCreativeModal = () => {
        setIsPreviewCreativeModalOpen(!isPreviewCreativeModalOpen);
    };

    const dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        timeZoneName: 'shortOffset',
    };

    const columns = [
        {
            header: '#',
            cell: (cell: any) => {
                return (
                    <span className="fw-semibold">{cell.getValue()}</span>
                );
            },
            accessorKey: 'id',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome',
            accessorKey: 'name',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tipo',
            accessorKey: 'type',
            cell: (cell: any) => {
                const creativeType = cell.getValue();
                const badgeColor = (() => {
                    switch (creativeType) {
                        case CreativeType.IMAGE:
                            return 'info';
                        case CreativeType.HTML5:
                            return 'primary';
                        case CreativeType.VIDEO_VAST:
                            return 'warning';
                        case CreativeType.AUDIO_VAST:
                            return 'success';
                        default:
                            return 'danger';
                    }
                })();
                return <span className={`badge bg-${badgeColor}`}>{creativeType.split('_')[0] || 'não encontrado'}</span>;
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Tamanho',
            accessorKey: 'size',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Pedidos',
            accessorKey: 'orders.length',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Criado em",
            accessorFn: (row: any) => {
                const date = new Date(row.createdAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Atualizado em",
            accessorFn: (row: any) => {
                const date = new Date(row.updatedAt);
                return date.toLocaleDateString('pt-BR', dateFormatOptions);
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Master Creatives ID",
            accessorFn: (row: any) => {
                const masterCreatives = row.masterCreatives || [];
                return masterCreatives.length > 0 ? masterCreatives.map((mc: any) => mc.id).join(', ') : '-';
            },
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Companion Creative ID",
            accessorFn: (row: any) => row.companionAd?.id ?? '-',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                let actionCell = [];

                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    // Botão de deletar
                    actionCell.push(<div key="action_delete">
                        <button className="btn text-danger d-inline-block"
                                id={'action_delete_' + row.id}
                                onClick={() => {
                                    deleteCreative(row).then(() => {
                                        toast.success('Criativo deletado com sucesso');
                                        setLoading(true);
                                        getCreatives().then(setData).finally(() => setLoading(false));
                                    });
                                }}
                        ><i className="ri-delete-bin-2-fill fs-16"></i></button>
                        <UncontrolledTooltip
                            placement="top"
                            target={'action_delete_' + row.id}
                        >
                            Deletar
                        </UncontrolledTooltip>
                    </div>);
                }

                // Botão preview
                actionCell.push(<div key="action_preview">
                    <button className="btn text-primary d-inline-block"
                            id={'action_preview_' + row.id}
                            onClick={() => {
                                setDataToEdit(row);
                                togglePreviewCreativeModal();
                            }}
                    ><i className="ri-eye-fill fs-16"></i></button>
                    <UncontrolledTooltip
                        placement="top"
                        target={'action_preview_' + row.id}
                    >
                        Preview
                    </UncontrolledTooltip>
                </div>);
                return <div className="d-flex gap-2">{actionCell}</div>;
            },
            enableColumnFilter: false,
        },
    ];

    document.title = 'Criativos | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardHeader>
                                    <Row className="g-4 align-items-center">
                                        <div className="col-sm">
                                            <h5 className="card-title mb-0">Criativos</h5>
                                        </div>
                                        <div className="col-sm-auto">
                                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-success add-btn me-1"
                                                    id="create-btn"
                                                    onClick={() => {
                                                        setDataToEdit(null);
                                                        toggleAddEditModal();
                                                    }}
                                                >
                                                    <i className="ri-add-line align-bottom me-1"></i> Adicionar criativo
                                                </button>
                                            )}
                                        </div>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    {!loading ? <TableContainer
                                                divClass="table-responsive"
                                                columns={(columns || [])}
                                                data={(data || [])}
                                                isGlobalFilter={true}
                                                customPageSize={30}
                                                SearchPlaceholder="Procurar..."
                                            /> :
                                        <div className="text-center">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Carregando...</span>
                                            </div>
                                        </div>}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            <AddEditCreativeModal
                creative={dataToEdit}
                isOpen={isAddEditModalOpen}
                toggle={toggleAddEditModal}
                onUpdateData={handleAddUpdateData}
                onAddData={handleAddUpdateData}
            />

            {dataToEdit && (
                <PreviewCreativeModal
                    creative={dataToEdit}
                    isOpen={isPreviewCreativeModalOpen}
                    toggle={togglePreviewCreativeModal}
                />
            )}
        </React.Fragment>
    );
};

export default withRouter(Index);